import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { MLComparisonData } from '../components/charts/ml-comparison-chart/ml-comparison-chart.component';
import { secureRandom } from '../util/secure-random.util';

@Injectable({
  providedIn: 'root'
})
export class MLComparisonService {
  private readonly API_BASE_URL = '/api/v1'; // This would be your actual API base URL

  /**
   * Get ML vs Legacy comparison data
   * This simulates a real API call with realistic delay
   */
  getMLComparisonData(): Observable<MLComparisonData> {
    // Simulate API call with realistic delay
    return of(true).pipe(
      delay(500 + secureRandom() * 500), // Reduced delay between 0.5-1 seconds
      map(() => this.generateMockData())
    );
  }

  /**
   * Generate realistic mock data
   * In real implementation, this data would come from the actual API response
   */
  private generateMockData(): MLComparisonData {
    // Add some variability to make it more realistic
    const baseLeadTimeLegacy = 45;
    const baseLeadTimeML = 38;
    const baseCoverageLegacy = 82;
    const baseCoverageML = 94;

    const leadTimeLegacy = baseLeadTimeLegacy + (secureRandom() - 0.5) * 5;
    const leadTimeML = baseLeadTimeML + (secureRandom() - 0.5) * 3;
    const coverageLegacy = baseCoverageLegacy + (secureRandom() - 0.5) * 4;
    const coverageML = baseCoverageML + (secureRandom() - 0.5) * 2;

    // Calculate improvement percentage
    const leadTimeImprovement = ((leadTimeLegacy - leadTimeML) / leadTimeLegacy) * 100;
    const coverageImprovement = ((coverageML - coverageLegacy) / coverageLegacy) * 100;
    const overallImprovement = (leadTimeImprovement + coverageImprovement) / 2;

    return {
      avgLeadTimeLegacy: Number(leadTimeLegacy.toFixed(1)),
      avgLeadTimeML: Number(leadTimeML.toFixed(1)),
      coverageLegacy: Number(coverageLegacy.toFixed(1)),
      coverageML: Number(coverageML.toFixed(1)),
      improvement: Number(overallImprovement.toFixed(2)),
      totalSkuLocations: 12487 + Math.floor(secureRandom() * 1000) // Add some variance
    };
  }

  /**
   * Real API implementation would look like this:
   *
   * getMLComparisonData(): Observable<MLComparisonData> {
   *   return this.http.get<MLComparisonData>(`${this.API_BASE_URL}/ml-comparison`)
   *     .pipe(
   *       timeout(10000), // 10 second timeout
   *       retry(2), // Retry up to 2 times on failure
   *       catchError(this.handleError)
   *     );
   * }
   *
   * private handleError(error: any): Observable<never> {
   *   console.error('ML Comparison API Error:', error);
   *   return throwError(() => new Error('Failed to load ML comparison data. Please try again.'));
   * }
   */
}
